import { get, maxBy } from 'lodash';
import { takeLatest, put, call, select } from 'redux-saga/effects';
import { selectUserData } from '../../App/selectors';
import { getSettingsSaga } from '../../App/saga';
import {
  cancelSubscriptionAction,
  reactivateSubscriptionAction,
  updateSettingsProperty,
  changePasswordAction,
  getPaymentMethodsAction,
  getProductsAction,
  getUserSubscriptionsAction,
  getUserInvoicesAction,
  getUserPaymentsAction,
  updateSubscriptionAction,
  getAllProductsAction,
} from './actions';
import {
  cancelUserSubscription,
  changePassword,
  getUserPaymentMethods,
  getUserInvoices,
  getUserPayments,
  reactivateUserSubscription,
  updateUserSubscription,
} from '../../../utils/API';
import { selectProducts } from './selectors';
const { updateSettingsField, getProductsList, getUserSubscriptions } = require('../../../utils/API');

function* updateSettingsPropertySaga({ payload }) {
  try {
    const user = yield select(selectUserData());
    const id = get(user, 'id');
    if (id) {
      yield call(updateSettingsField, id, get(payload, 'identifier'), get(payload, 'value'));
      yield call(getSettingsSaga, id);
      if (payload.callback && typeof payload.callback === 'function') {
        payload.callback({ status: 'ok' });
      }
    }
  } catch (err) {
    yield put(updateSettingsProperty.failure(err));
    if (payload.callback && typeof payload.callback === 'function') {
      payload.callback({ status: 'error' });
    }
  } finally {
    yield put(updateSettingsProperty.fulfill());
  }
}

function* changePasswordSaga({ payload, serverCookies }) {
  try {
    const userId = get(payload, 'user.id');
    const oldPassword = get(payload, 'password');
    const newPassword = get(payload, 'newPassword');
    const response = yield call(changePassword, userId, oldPassword, newPassword, serverCookies);
    yield put(changePasswordAction.success(response.data));
    if (payload.callback && typeof payload.callback === 'function') {
      payload.callback({ status: 'ok' });
    }
  } catch (err) {
    yield put(changePasswordAction.failure(err));
    if (payload.callback && typeof payload.callback === 'function') {
      payload.callback({ status: 'error' });
    }
  } finally {
    yield put(changePasswordAction.fulfill());
  }
}

function* getUserBankCardsSaga({ payload, serverCookies }) {
  try {
    const userData = yield select(selectUserData());
    const userId = get(userData, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const response = yield call(getUserPaymentMethods, userId, serverCookies);
    if (payload?.callback) {
      const methods = get(response, 'data.data', []);
      const newestMethod = maxBy(methods, met => met.id);
      if (newestMethod) {
        payload.callback(newestMethod);
      }
    }
    yield put(getPaymentMethodsAction.success(get(response, 'data')));
  } catch (e) {
    yield put(getPaymentMethodsAction.failure(e));
  } finally {
    yield put(getPaymentMethodsAction.fulfill());
  }
}

function* getProductsSaga({ serverCookies }) {
  try {
    const products = yield select(selectProducts());
    if (!products.loadedOnce) {
      const response = yield call(getProductsList, serverCookies);
      yield put(getProductsAction.success(get(response, 'data', [])));
    }
  } catch (e) {
    yield put(getProductsAction.failure(e));
  } finally {
    yield put(getProductsAction.fulfill());
  }
}

function* getAllProductsSaga({ serverCookies }) {
  try {
    const response = yield call(getProductsList, serverCookies, [
      'subscription',
      'excluded_subscription',
      'legacy_subscription',
    ]);
    yield put(getProductsAction.success(get(response, 'data', [])));
  } catch (e) {
    yield put(getProductsAction.failure(e));
  } finally {
    yield put(getProductsAction.fulfill());
  }
}

function* getUserSubscriptionsSaga({ serverCookies }) {
  try {
    const userData = yield select(selectUserData());
    const userId = get(userData, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const response = yield call(getUserSubscriptions, userId, serverCookies);
    yield put(getUserSubscriptionsAction.success(get(response, 'data', [])));
  } catch (e) {
    yield put(getUserSubscriptionsAction.failure(e));
  } finally {
    yield put(getUserSubscriptionsAction.fulfill());
  }
}

function* getUserInvoicesSaga({ payload, serverCookies }) {
  try {
    const userData = yield select(selectUserData());
    const userId = get(userData, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const response = yield call(getUserInvoices, userId, serverCookies);
    yield put(getUserInvoicesAction.success(get(response, 'data', [])));
  } catch (e) {
    yield put(getUserInvoicesAction.failure(e));
  } finally {
    yield put(getUserInvoicesAction.fulfill());
  }
}

function* getUserPaymentsSaga({ payload, serverCookies }) {
  try {
    const userData = yield select(selectUserData());
    const userId = get(userData, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const response = yield call(getUserPayments, userId, payload?.query || {}, serverCookies);
    yield put(getUserPaymentsAction.success(get(response, 'data', [])));
  } catch (e) {
    yield put(getUserPaymentsAction.failure(e));
  } finally {
    yield put(getUserPaymentsAction.fulfill());
  }
}

function* cancelSubscriptionSaga({ payload, serverCookies }) {
  try {
    if (!payload.userId) {
      throw new Error('User undefined');
    }
    if (!payload.subId) {
      throw new Error('Subscription id is undefined');
    }
    const response = yield call(
      cancelUserSubscription,
      {
        query: { userId: payload.userId, subscriptionId: payload.subId },
        data: {
          cancelReason: payload.reason,
          note: payload.note,
        },
      },
      serverCookies,
    );
    yield call(getUserSubscriptionsSaga, { serverCookies });
    yield put(cancelSubscriptionAction.success());
  } catch (e) {
    yield put(cancelSubscriptionAction.failure(e));
  } finally {
    yield put(cancelSubscriptionAction.fulfill());
  }
}

function* reactivateSubscriptionSaga({ payload, serverCookies }) {
  try {
    if (!payload.userId) {
      throw new Error('User undefined');
    }
    if (!payload.subId) {
      throw new Error('Subscription id is undefined');
    }
    const response = yield call(
      reactivateUserSubscription,
      payload.userId,
      payload.subId,
      payload.isTrialReactivation ? 'in_trial' : 'active',
      serverCookies,
    );
    if (payload.callback) {
      payload.callback({ status: 'ok' });
    }
    // yield call(getUserSubscriptionsSaga, { serverCookies });
    yield put(reactivateSubscriptionAction.success());
  } catch (e) {
    if (payload.callback) {
      payload.callback({ status: 'fail', error: e });
    }
    yield put(reactivateSubscriptionAction.failure(e));
  } finally {
    yield put(reactivateSubscriptionAction.fulfill());
  }
}

function* updateSubscriptionSaga({ payload, serverCookies }) {
  try {
    if (!payload.userId) {
      throw new Error('User undefined');
    }
    if (!payload.subId) {
      throw new Error('Subscription id is undefined');
    }
    const response = yield call(updateUserSubscription, payload.userId, payload.subId, payload.data, serverCookies);
    if (payload.callback) {
      payload.callback({ status: 'ok' });
    }
    // yield call(getUserSubscriptionsSaga, { serverCookies });
    yield put(reactivateSubscriptionAction.success());
  } catch (e) {
    if (payload.callback) {
      payload.callback({ status: 'fail', error: e });
    }
    yield put(reactivateSubscriptionAction.failure(e));
  } finally {
    yield put(reactivateSubscriptionAction.fulfill());
  }
}

export default function* accountSettingsSaga() {
  yield takeLatest(updateSettingsProperty.TRIGGER, updateSettingsPropertySaga);
  yield takeLatest(reactivateSubscriptionAction.TRIGGER, reactivateSubscriptionSaga);
  // yield takeLatest(cancelSubscriptionAction.TRIGGER, updateSettingsPropertySaga);
  yield takeLatest(changePasswordAction.TRIGGER, changePasswordSaga);
  yield takeLatest(getPaymentMethodsAction.TRIGGER, getUserBankCardsSaga);
  yield takeLatest(getProductsAction.TRIGGER, getProductsSaga);
  yield takeLatest(getAllProductsAction.TRIGGER, getAllProductsSaga);
  yield takeLatest(getUserSubscriptionsAction.TRIGGER, getUserSubscriptionsSaga);
  yield takeLatest(getUserInvoicesAction.TRIGGER, getUserInvoicesSaga);
  yield takeLatest(getUserPaymentsAction.TRIGGER, getUserPaymentsSaga);
  yield takeLatest(cancelSubscriptionAction.TRIGGER, cancelSubscriptionSaga);
  yield takeLatest(updateSubscriptionAction.TRIGGER, updateSubscriptionSaga);
}
